import Component from '@glimmer/component'
import { colors } from '@colors'
import type {
  AxisLabelsFormatterContextObject,
  Options,
  PlotOptions,
  XAxisOptions,
  YAxisOptions,
  TooltipOptions,
  SeriesColumnOptions,
  Point,
} from 'highcharts'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import { LeftSplitChart } from 'teacher-ui-client/components/giraffe/left-split-chart'
import { SummaryBox, type Stat } from 'teacher-ui-client/components/giraffe/summary-box'
import Highcharter from 'teacher-ui-client/components/base/highcharter'
import { formatValue } from 'teacher-ui-client/utils/format-value'

function yAxisLabelFormatter(this: AxisLabelsFormatterContextObject) {
  const suffix = this.isLast ? '%' : ''
  return `${this.value}${suffix}`
}

function tooltipFormatter(this: Point) {
  const { event } = this as Point & { event: { attempts: number } }
  return `Average score: ${this.y}%<br>Attempts: ${event.attempts}`
}

export interface QuizScoresStudentData {
  summary: {
    average_score: Nullable<number>
    average_attempts: number
  }
  events: { attempts: number; average_score: number; lesson_number: number }[]
}

interface Args {
  summaryTitle: string
  data: QuizScoresStudentData
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class QuizScoresStudent extends Component<Signature> {
  readonly colorScheme = SummaryBoxColorScheme.Orange

  readonly plotOptions: PlotOptions = {
    column: {
      stacking: 'normal',
      borderWidth: 0,
      groupPadding: 0.2,
      pointPadding: 0.1,
      colorByPoint: false,
    },
  }

  get highchartOptions(): Options {
    return {
      chart: {
        type: 'column',
        plotBackgroundColor: colors.dustyBlack50,
        backgroundColor: 'transparent',
      },
      series: this.chartData,
      title: { text: '' },
      yAxis: this.yAxisOptions,
      xAxis: this.xAxisOptions,
      plotOptions: this.plotOptions,
      tooltip: this.tooltipOptions,
      legend: { enabled: false },
    }
  }

  get xAxisOptions(): XAxisOptions {
    return {
      title: { text: 'Lesson number' },
      categories: this.categories,
      visible: true,
      allowDecimals: false,
      tickWidth: 1,
      plotLines: [],
      plotBands: [],
      lineColor: colors.dustyBlack200,
      tickColor: colors.dustyBlack200,
    }
  }

  get yAxisOptions(): YAxisOptions {
    return {
      title: { text: 'Average score' },
      visible: true,
      min: 0,
      max: 100,
      plotLines: [],
      plotBands: [],
      reversedStacks: false,
      allowDecimals: false,
      gridLineColor: colors.dustyBlack100,
      endOnTick: true,
      labels: {
        formatter: yAxisLabelFormatter,
      },
    }
  }

  get summaryStats(): Stat[] {
    const { summary } = this.args.data
    const score = summary.average_score
    const attempts = summary.average_attempts

    return [
      { label: 'Average score', value: formatValue(score, { append: '%' }) },
      { label: 'Average attempts', value: `${attempts}` },
    ]
  }

  get categories() {
    return this.args.data.events.map((ev) => ev.lesson_number.toString())
  }

  get chartData(): SeriesColumnOptions[] {
    const data = this.args.data.events.map((event) => {
      const name = event.lesson_number.toString()
      const y = Number(event.average_score)

      let colorIndex
      if (y < 50) {
        colorIndex = 0 // red
      } else if (y < 80) {
        colorIndex = 1 // orange
      } else {
        colorIndex = 2 // green
      }

      const eventColors = [colors.watermelonyRed300, colors.juicyOrange300, colors.msGreen300]
      const color = eventColors[colorIndex]

      return { name, y, color, event }
    })

    return [{ data, type: 'column' }]
  }

  get tooltipOptions(): TooltipOptions {
    return {
      enabled: true,
      backgroundColor: colors.neutral400,
      borderWidth: 0,
      borderRadius: 5,
      shadow: false,
      formatter: tooltipFormatter,
      style: {
        color: colors.white,
      },
    }
  }

  <template>
    <LeftSplitChart ...attributes>
      <:left>
        <SummaryBox
          class="h-full"
          @title={{@summaryTitle}}
          @stats={{this.summaryStats}}
          @colorScheme={{this.colorScheme}}
        />
      </:left>
      <:right>
        <Highcharter class="h-full" @options={{this.highchartOptions}} />
      </:right>
    </LeftSplitChart>
  </template>
}

export default QuizScoresStudent
